import { IPublicBlogImage } from '@/infrastructure/interfaces/i-blog';

const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;
const SCHEME_PATTERN = /^([a-z][a-z\d+.-]*):/i;

export const isValidBlogSlug = (slug: unknown): slug is string => {
  if (typeof slug !== 'string' || !slug || slug !== slug.trim()) return false;
  if (slug.length > 200 || /[\\/?#%\s]/.test(slug)) return false;
  return slug !== '.' && slug !== '..';
};

export const getBlogPostPath = (slug: string) =>
  `/blog/${encodeURIComponent(slug)}`;

export const getPublishedBlogPostEndpoint = (slug: string) =>
  `/web/blog/posts/${encodeURIComponent(slug)}`;

export const isSafeLinkUrl = (url: string): boolean => {
  const value = url.trim();
  if (!value || value !== url || CONTROL_CHARACTER_PATTERN.test(value)) {
    return false;
  }
  if (value.startsWith('//')) return false;

  const scheme = value.match(SCHEME_PATTERN)?.[1]?.toLowerCase();
  return !scheme || ['http', 'https', 'mailto', 'tel'].includes(scheme);
};

export const isSafeImageUrl = (url: string): boolean => {
  const value = url.trim();
  if (!value || value !== url || CONTROL_CHARACTER_PATTERN.test(value)) {
    return false;
  }
  if (value.startsWith('/')) return !value.startsWith('//');
  return value.toLowerCase().startsWith('https://');
};

const safeImageCandidate = (url: string | null | undefined) =>
  url && isSafeImageUrl(url) ? url : null;

export const getBlogImageUrl = (image: IPublicBlogImage | null) =>
  image
    ? safeImageCandidate(image.optimizedUrl) ||
      safeImageCandidate(image.originalUrl)
    : null;

export const getBlogImageSrcSet = (image: IPublicBlogImage | null) => {
  if (!image) return undefined;

  const candidates = [
    [safeImageCandidate(image.smallUrl), '480w'],
    [safeImageCandidate(image.mediumUrl), '768w'],
    [getBlogImageUrl(image), '1200w'],
  ].filter((entry): entry is [string, string] => Boolean(entry[0]));

  const unique = new Map(candidates.map(([url, width]) => [url, width]));
  return unique.size
    ? Array.from(unique, ([url, width]) => `${url} ${width}`).join(', ')
    : undefined;
};
